import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  Cog8ToothIcon
} from "@heroicons/react/24/outline";

const FAQPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  const faqData = [
    {
      category: "Getting Started",
      icon: QuestionMarkCircleIcon,
      color: "from-brand to-brand-dark",
      questions: [
        {
          id: 1,
          question: "How do I get started with Tanikkai?",
          answer: "Sign up on our website, follow onboarding steps, or request a demo for assistance. Our team will guide you through the setup process and help customize the platform to your specific compliance needs."
        }
      ]
    },
    {
      category: "Privacy & Compliance",
      icon: ShieldCheckIcon,
      color: "from-green-500 to-emerald-500",
      questions: [
        {
          id: 2,
          question: "How can I raise a privacy or compliance concern?",
          answer: "Submit concerns via our support portal or contact our compliance team. We have dedicated channels for privacy-related issues and ensure prompt response to all compliance concerns."
        },
        {
          id: 3,
          question: "Does Tanikkai comply with global privacy regulations?",
          answer: "Yes, Tanikkai complies with GDPR, CCPA, HIPAA, and other regulations. Our platform is designed with privacy-by-design principles and supports compliance across 20+ major frameworks."
        },
        {
          id: 4,
          question: "How does Tanikkai handle personal and sensitive data?",
          answer: "We use encryption, access controls, and anonymization for secure data processing. All data is encrypted both in transit and at rest, with role-based access controls ensuring only authorized personnel can access sensitive information."
        },
        {
          id: 5,
          question: "Can Tanikkai assist with privacy audits or legal investigations?",
          answer: "Yes, we provide audit logs, compliance reports, and expert guidance. Our platform maintains comprehensive audit trails and can generate detailed reports for regulatory investigations and compliance audits."
        },
        {
          id: 6,
          question: "How does Tanikkai handle cookies and tracking technologies?",
          answer: "We offer cookie management solutions ensuring regulatory compliance. Our platform includes consent management tools and helps organizations maintain compliant tracking practices across their digital properties."
        },
        {
          id: 7,
          question: "Can Tanikkai help manage consent for marketing purposes?",
          answer: "Yes, our platform tracks, manages, and automates user consent. We provide comprehensive consent management capabilities for marketing, analytics, and other data processing activities."
        },
        {
          id: 8,
          question: "How does Tanikkai ensure data minimization compliance?",
          answer: "Our AI automates data minimization and risk assessments. The platform continuously monitors data usage patterns and provides recommendations for reducing data collection to only what's necessary."
        },
        {
          id: 9,
          question: "How does Tanikkai manage privacy risks related to third-party vendors?",
          answer: "We provide vendor assessments, compliance monitoring, and automated risk management. Our TPRM module includes privacy impact assessments and continuous monitoring of vendor compliance status."
        },
        {
          id: 10,
          question: "What happens if there's a privacy breach or data incident?",
          answer: "We offer real-time alerts, automated incident response, and compliance reporting. Our incident management system can automate notifications to regulatory authorities and provide breach assessment tools."
        },
        {
          id: 11,
          question: "Can Tanikkai automate compliance reporting?",
          answer: "Yes, our platform generates real-time compliance reports and audit logs automatically. You can schedule regular reports for different frameworks and customize them based on your specific requirements."
        },
        {
          id: 12,
          question: "Does Tanikkai support multi-jurisdictional compliance management?",
          answer: "Yes, we help businesses comply with multiple regulations across different regions. Our platform maps requirements across jurisdictions and provides unified compliance management for global operations."
        }
      ]
    },
    {
      category: "Security & Incidents",
      icon: ExclamationTriangleIcon,
      color: "from-red-500 to-orange-500",
      questions: [
        {
          id: 13,
          question: "How does Tanikkai handle security breaches or incidents?",
          answer: "We detect, investigate, and mitigate threats with AI-driven security. Our platform includes automated incident detection, response workflows, and integration with security orchestration tools."
        },
        {
          id: 14,
          question: "What should I do if I'm unsure about my integration's security compliance?",
          answer: "Request an assessment or use Tanikkai's automated compliance tools. Our security experts can conduct comprehensive assessments and provide recommendations for improving your security posture."
        },
        {
          id: 15,
          question: "How does Tanikkai's AI-powered platform strengthen data protection?",
          answer: "It continuously monitors threats and enforces security policies. Our AI algorithms analyze patterns, detect anomalies, and automatically apply security controls to protect sensitive data."
        },
        {
          id: 16,
          question: "How does Tanikkai ensure system uptime and business continuity during emergencies?",
          answer: "We use redundant infrastructure, disaster recovery, and real-time monitoring. Our platform is built on enterprise-grade infrastructure with 99.9% uptime SLA and comprehensive disaster recovery procedures."
        }
      ]
    },
    {
      category: "Integrations & Support",
      icon: Cog8ToothIcon,
      color: "from-purple-500 to-pink-500",
      questions: [
        {
          id: 17,
          question: "How can I request a new integration with Tanikkai?",
          answer: "Submit a request via our support portal or customer success team. We regularly add new integrations based on customer needs and can provide custom integration development for enterprise clients."
        },
        {
          id: 18,
          question: "How can I check the status of my integration request or issue?",
          answer: "Track requests through our dashboard or contact support. We provide real-time status updates and maintain transparent communication throughout the integration process."
        },
        {
          id: 19,
          question: "What kind of support is available after implementation?",
          answer: "We offer live chat, email support, and dedicated account managers. Our support team is available 24/7 for critical issues, with comprehensive documentation and training resources."
        },
        {
          id: 20,
          question: "Does Tanikkai offer training for compliance and security teams?",
          answer: "Yes, we provide webinars, training sessions, and certification programs. Our learning management system includes 82+ training modules covering various compliance and security topics."
        }
      ]
    }
  ];

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredFAQs = faqData
    .map(category => ({
      ...category,
      questions: category.questions.filter(q =>
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }))
    .filter(category => 
      selectedCategory === 'all' || 
      category.category.toLowerCase() === selectedCategory.toLowerCase()
    )
    .filter(category => category.questions.length > 0);

  const categories = ['all', ...faqData.map(cat => cat.category.toLowerCase())];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative px-6 pt-12 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full border border-white/20 mb-6">
              <QuestionMarkCircleIcon className="w-5 h-5 text-cyan-400 mr-2" />
              <span className="text-sm font-medium text-cyan-200">Frequently Asked Questions</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              How Can We
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent block">
                Help You?
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
              Find answers to common questions about Tanikkai's AI-powered compliance platform, 
              security features, integrations, and support services.
            </p>

            <div className="flex items-center justify-center mb-12">
              <img 
                src="https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg" 
                alt="Professional Help and Support Technology"
                className="max-w-2xl w-full h-auto rounded-2xl shadow-2xl border border-white/20"
              />
            </div>
          </motion.div>
        </div>

        {/* Background Elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="relative px-6 py-12 bg-gradient-to-r from-slate-900/50 to-blue-900/30 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 mb-8">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="md:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category} value={category} className="bg-slate-700">
                    {category === 'all' ? 'All Categories' : category.charAt(0).toUpperCase() + category.slice(1).replace('&', ' & ')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Counter */}
          {searchTerm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-6"
            >
              <p className="text-gray-400 text-sm">
                Found {filteredFAQs.reduce((total, cat) => total + cat.questions.length, 0)} results for "{searchTerm}"
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* FAQ Content */}
      <section className="relative px-6 py-20">
        <div className="max-w-4xl mx-auto">
          {filteredFAQs.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <QuestionMarkCircleIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No results found</h3>
              <p className="text-gray-400">Try adjusting your search terms or selecting a different category.</p>
            </motion.div>
          ) : (
            <div className="space-y-12">
              {filteredFAQs.map((category, categoryIndex) => (
                <motion.div
                  key={categoryIndex}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                  viewport={{ once: true }}
                >
                  {/* Category Header */}
                  <div className="flex items-center mb-8">
                    <div className={`w-12 h-12 bg-gradient-to-r ${category.color} rounded-xl flex items-center justify-center mr-4`}>
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white">{category.category}</h2>
                      <p className="text-gray-400 text-sm">{category.questions.length} question{category.questions.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>

                  {/* Questions */}
                  <div className="space-y-4">
                    {category.questions.map((item, itemIndex) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: itemIndex * 0.05 }}
                        viewport={{ once: true }}
                        className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl border border-white/20 hover:border-white/40 transition-all duration-300 overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(item.id)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <h3 className="text-lg font-semibold text-white pr-4">
                            {item.question}
                          </h3>
                          <ChevronDownIcon 
                            className={`w-5 h-5 text-cyan-400 transition-transform flex-shrink-0 ${
                              openItems[item.id] ? 'rotate-180' : ''
                            }`} 
                          />
                        </button>
                        
                        <AnimatePresence>
                          {openItems[item.id] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 pb-6">
                                <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-4"></div>
                                <p className="text-gray-300 leading-relaxed">
                                  {item.answer}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-cyan-900/50 to-blue-900/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Still Have Questions?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Can't find what you're looking for? Our support team is here to help you 24/7.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <motion.a
                href="mailto:support@habilesec.com"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-2xl border-2 border-white/20"
              >
                Contact Support
              </motion.a>
              
              <motion.a
                href="tel:+91-435-290-0535"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30"
              >
                Call Us Now
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;